<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="relative bg-white min-h-screen flex items-center overflow-hidden">
      <!-- Floating Icons -->
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute top-20 left-16 animate-bounce delay-75">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Lightbulb class="w-8 h-8 text-white" />
          </div>
        </div>
        <div class="absolute top-32 right-24 animate-bounce delay-150">
          <div class="w-12 h-12 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
            <Star class="w-6 h-6 text-white" />
          </div>
        </div>
        <div class="absolute bottom-40 left-32 animate-bounce delay-300">
          <div class="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
            <TrendingUp class="w-7 h-7 text-white" />
          </div>
        </div>
        <div class="absolute top-1/2 right-16 animate-bounce delay-500">
          <div class="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center shadow-lg">
            <Award class="w-5 h-5 text-white" />
          </div>
        </div>
        
        <!-- Floating Curves -->
        <svg class="absolute top-0 left-0 w-full h-full" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M-100 200C200 150 400 250 600 200C800 150 1000 250 1300 200V0H-100V200Z" fill="url(#gradient1)" opacity="0.1"/>
          <path d="M-100 400C200 350 400 450 600 400C800 350 1000 450 1300 400V600H-100V400Z" fill="url(#gradient2)" opacity="0.1"/>
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#667eea"/>
              <stop offset="100%" stop-color="#764ba2"/>
            </linearGradient>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#f093fb"/>
              <stop offset="100%" stop-color="#f5576c"/>
            </linearGradient>
          </defs>
        </svg>
      </div>
      
      <div class="container mx-auto px-6 relative z-10 py-20">
        <div class="max-w-4xl mx-auto text-center">
          <h1 class="text-6xl md:text-8xl font-black mb-6 bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 bg-clip-text text-transparent leading-tight">
            Pitch Your Dream
          </h1>
          <p class="text-2xl md:text-3xl mb-8 text-gray-700 font-medium">
            Turn Your <span class="text-purple-600 font-bold">Entrepreneurial Vision</span> Into Reality
          </p>
          <p class="text-lg mb-12 max-w-2xl mx-auto text-gray-600 leading-relaxed">
            The Mshel Foundation's flagship grant program empowering entrepreneurs with innovative business ideas to transform their communities and create lasting impact.
          </p>
          <div class="flex flex-col sm:flex-row gap-6 justify-center">
            <button class="group relative px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl font-bold text-lg shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 transform hover:scale-105">
              <span class="relative z-10">Apply Now</span>
              <div class="absolute inset-0 bg-gradient-to-r from-purple-700 to-blue-700 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
            <button class="px-10 py-4 border-2 border-gray-900 text-gray-900 rounded-2xl font-bold text-lg hover:bg-gray-900 hover:text-white transition-all duration-300 transform hover:scale-105">
              Learn More
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Program Overview -->
    <section class="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div class="container mx-auto px-6">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-5xl font-bold mb-8 text-gray-900">About Pitch Your Dream</h2>
          <p class="text-xl leading-relaxed mb-8 text-gray-700">
            Pitch Your Dream is more than just a grant program—it's a <span class="text-purple-600 font-semibold">launchpad for visionary entrepreneurs</span> who dare to dream big and have the determination to make those dreams reality.
          </p>
          <p class="text-lg leading-relaxed text-gray-600">
            We believe that great ideas deserve great support. Our program identifies and nurtures innovative business initiatives that have the potential to create positive change in their communities and beyond.
          </p>
        </div>
      </div>
    </section>

    <!-- Recent Events -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-6">
        <h2 class="text-5xl font-bold text-center mb-16 text-gray-900">Recent Events</h2>
        <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div class="group cursor-pointer">
            <div class="relative overflow-hidden rounded-3xl shadow-2xl transform group-hover:scale-105 transition-all duration-500">
              <div class="h-64 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500"></div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div class="absolute bottom-6 left-6 right-6 text-white">
                <h3 class="text-xl font-bold mb-2">2024 Pitch Competition Finals</h3>
                <p class="text-sm opacity-90">50+ entrepreneurs competed for ₦10M in grants</p>
                <span class="text-xs bg-white/20 px-3 py-1 rounded-full mt-3 inline-block">December 2024</span>
              </div>
            </div>
          </div>
          
          <div class="group cursor-pointer">
            <div class="relative overflow-hidden rounded-3xl shadow-2xl transform group-hover:scale-105 transition-all duration-500">
              <div class="h-64 bg-gradient-to-br from-green-400 via-blue-500 to-purple-500"></div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div class="absolute bottom-6 left-6 right-6 text-white">
                <h3 class="text-xl font-bold mb-2">Mentorship Bootcamp</h3>
                <p class="text-sm opacity-90">3-day intensive training with industry experts</p>
                <span class="text-xs bg-white/20 px-3 py-1 rounded-full mt-3 inline-block">November 2024</span>
              </div>
            </div>
          </div>
          
          <div class="group cursor-pointer">
            <div class="relative overflow-hidden rounded-3xl shadow-2xl transform group-hover:scale-105 transition-all duration-500">
              <div class="h-64 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500"></div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div class="absolute bottom-6 left-6 right-6 text-white">
                <h3 class="text-xl font-bold mb-2">Alumni Success Summit</h3>
                <p class="text-sm opacity-90">Celebrating 100+ successful startups launched</p>
                <span class="text-xs bg-white/20 px-3 py-1 rounded-full mt-3 inline-block">October 2024</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Features -->
    <section class="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
      <div class="container mx-auto px-6">
        <h2 class="text-5xl font-bold text-center mb-16 text-white">What We Offer</h2>
        <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div class="bg-white/10 backdrop-blur-lg p-8 rounded-3xl border border-white/20 text-center group hover:bg-white/20 transition-all duration-300">
            <div class="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold mb-4 text-white">Financial Grant</h3>
            <p class="text-gray-300">
              Receive substantial funding to kickstart your business venture and cover initial operational costs.
            </p>
          </div>
          
          <div class="bg-white/10 backdrop-blur-lg p-8 rounded-3xl border border-white/20 text-center group hover:bg-white/20 transition-all duration-300">
            <div class="w-20 h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <Users class="w-10 h-10 text-white" />
            </div>
            <h3 class="text-2xl font-bold mb-4 text-white">Mentorship Program</h3>
            <p class="text-gray-300">
              Get paired with experienced entrepreneurs and industry experts who will guide your journey.
            </p>
          </div>
          
          <div class="bg-white/10 backdrop-blur-lg p-8 rounded-3xl border border-white/20 text-center group hover:bg-white/20 transition-all duration-300">
            <div class="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9 3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold mb-4 text-white">Network Access</h3>
            <p class="text-gray-300">
              Join an exclusive community of innovators, investors, and changemakers in your industry.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Eligibility Criteria -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-6">
        <div class="max-w-6xl mx-auto">
          <h2 class="text-5xl font-bold text-center mb-16 text-gray-900">Eligibility Criteria</h2>
          <div class="grid md:grid-cols-2 gap-12">
            <div class="space-y-8">
              <div v-for="(item, index) in eligibilityLeft" :key="index" class="flex items-start space-x-6 group">
                <div class="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300"></div>
                <div>
                  <h4 class="font-bold text-xl text-gray-900 mb-3">{{ item.title }}</h4>
                  <p class="text-gray-600 leading-relaxed">{{ item.desc }}</p>
                </div>
              </div>
            </div>
            <div class="space-y-8">
              <div v-for="(item, index) in eligibilityRight" :key="index" class="flex items-start space-x-6 group">
                <div class="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300"></div>
                <div>
                  <h4 class="font-bold text-xl text-gray-900 mb-3">{{ item.title }}</h4>
                  <p class="text-gray-600 leading-relaxed">{{ item.desc }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Success Stories -->
    <section class="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div class="container mx-auto px-6">
        <h2 class="text-5xl font-bold text-center mb-16 text-gray-900">Success Stories</h2>
        <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div v-for="(story, index) in successStories" :key="index" class="bg-white p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div :class="`w-16 h-16 bg-gradient-to-br ${story.color} rounded-2xl flex items-center justify-center mb-6`">
              <span class="text-white font-bold text-2xl">{{ story.letter }}</span>
            </div>
            <h4 class="font-bold text-2xl mb-4 text-gray-900">{{ story.name }}</h4>
            <p class="text-gray-600 mb-6 leading-relaxed italic">"{{ story.quote }}"</p>
            <p class="text-sm font-semibold text-purple-600">- {{ story.author }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Application Form -->
    <section class="py-20 bg-gradient-to-br from-gray-900 to-purple-900">
      <div class="container mx-auto px-6">
        <div class="max-w-2xl mx-auto text-center mb-12">
          <h2 class="text-5xl font-bold mb-6 text-white">Ready to Pitch Your Dream?</h2>
          <p class="text-xl text-gray-300">
            The next cohort is now accepting applications. Don't miss your chance to turn your vision into reality.
          </p>
        </div>
        
        <div class="max-w-2xl mx-auto bg-white/10 backdrop-blur-lg rounded-3xl p-10 border border-white/20">
          <form @submit.prevent="handleSubmit" class="space-y-8">
            <div class="grid md:grid-cols-2 gap-6">
              <div>
                <label class="block text-white font-semibold mb-3 text-lg">Full Name <span class="text-red-400">*</span></label>
                <input 
                  type="text" 
                  v-model="formData.fullName"
                  class="w-full px-6 py-4 bg-white/20 border border-white/30 rounded-2xl text-white placeholder-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm" 
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label class="block text-white font-semibold mb-3 text-lg">Email Address <span class="text-red-400">*</span></label>
                <input 
                  type="email" 
                  v-model="formData.email"
                  class="w-full px-6 py-4 bg-white/20 border border-white/30 rounded-2xl text-white placeholder-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm" 
                  placeholder="your.email@example.com"
                />
              </div>
            </div>
            
            <div class="grid md:grid-cols-2 gap-6">
              <div>
                <label class="block text-white font-semibold mb-3 text-lg">Phone Number <span class="text-red-400">*</span></label>
                <input 
                  type="tel" 
                  v-model="formData.phone"
                  class="w-full px-6 py-4 bg-white/20 border border-white/30 rounded-2xl text-white placeholder-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm" 
                  placeholder="+234 XXX XXX XXXX"
                />
              </div>
              
              <div>
                <label class="block text-white font-semibold mb-3 text-lg">Business Name</label>
                <input 
                  type="text" 
                  v-model="formData.businessName"
                  class="w-full px-6 py-4 bg-white/20 border border-white/30 rounded-2xl text-white placeholder-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm" 
                  placeholder="Your business name"
                />
              </div>
            </div>
            
            <div>
              <label class="block text-white font-semibold mb-3 text-lg">Industry/Sector <span class="text-red-400">*</span></label>
              <select 
                v-model="formData.industry"
                class="w-full px-6 py-4 bg-white/20 border border-white/30 rounded-2xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
              >
                <option value="" class="text-gray-900">Select your industry</option>
                <option value="technology" class="text-gray-900">Technology</option>
                <option value="healthcare" class="text-gray-900">Healthcare</option>
                <option value="education" class="text-gray-900">Education</option>
                <option value="agriculture" class="text-gray-900">Agriculture</option>
                <option value="fintech" class="text-gray-900">Fintech</option>
                <option value="e-commerce" class="text-gray-900">E-commerce</option>
                <option value="other" class="text-gray-900">Other</option>
              </select>
            </div>
            
            <div>
              <label class="block text-white font-semibold mb-3 text-lg">Brief Business Description <span class="text-red-400">*</span></label>
              <textarea 
                rows="5" 
                v-model="formData.description"
                class="w-full px-6 py-4 bg-white/20 border border-white/30 rounded-2xl text-white placeholder-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm resize-none" 
                placeholder="Tell us about your business idea in 2-3 sentences..."
              ></textarea>
            </div>
            
            <div class="flex items-center space-x-3">
              <input 
                type="checkbox" 
                id="terms" 
                v-model="formData.termsAccepted"
                class="w-6 h-6 text-purple-600 rounded-lg"
              />
              <label for="terms" class="text-white text-sm">
                I agree to the <a href="#" class="text-purple-300 underline hover:text-purple-200">Terms and Conditions</a> and <a href="#" class="text-purple-300 underline hover:text-purple-200">Privacy Policy</a>
              </label>
            </div>
            
            <button 
              type="submit" 
              class="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              Submit Application
            </button>
          </form>
        </div>
      </div>
    </section>

    <!-- Important Dates -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-6">
        <h2 class="text-5xl font-bold text-center mb-16 text-gray-900">Important Dates</h2>
        <div class="max-w-6xl mx-auto">
          <div class="grid md:grid-cols-4 gap-8">
            <div v-for="(item, index) in importantDates" :key="index" class="bg-white p-8 rounded-3xl shadow-xl text-center group hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div :class="`text-4xl font-black mb-4 bg-gradient-to-r ${item.color} bg-clip-text text-transparent`">{{ item.date }}</div>
              <h4 class="font-bold text-xl mb-3 text-gray-900">{{ item.title }}</h4>
              <p class="text-gray-600 text-sm leading-relaxed">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
      <div class="container mx-auto px-6">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-5xl font-bold mb-8 text-white">Need Help?</h2>
          <p class="text-xl mb-16 text-gray-300">
            Our team is here to support you throughout your application process
          </p>
          
          <div class="grid md:grid-cols-3 gap-8">
            <div v-for="(contact, index) in contactInfo" :key="index" class="text-center group">
              <div :class="`w-20 h-20 bg-gradient-to-br ${contact.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`">
                <component :is="contact.icon" class="w-10 h-10 text-white" />
              </div>
              <h4 class="font-bold text-xl mb-3 text-white">{{ contact.title }}</h4>
              <p class="text-gray-300">{{ contact.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { 
  Star, 
  Lightbulb, 
  TrendingUp, 
  Users, 
  Award,  
  Mail, 
  Phone, 
  MapPin 
} from 'lucide-vue-next'

// Reactive form data
const formData = reactive({
  fullName: '',
  email: '',
  phone: '',
  businessName: '',
  industry: '',
  description: '',
  termsAccepted: false
})

// Eligibility criteria data
const eligibilityLeft = ref([
  { 
    title: "Innovative Business Idea", 
    desc: "Your venture must demonstrate innovation and potential for positive impact." 
  },
  { 
    title: "Early Stage", 
    desc: "Ideal for startups in pre-seed or seed stage looking for initial funding." 
  },
  { 
    title: "Committed Leadership", 
    desc: "Demonstrate strong leadership and commitment to your venture's success." 
  }
])

const eligibilityRight = ref([
  { 
    title: "Scalable Model", 
    desc: "Your business should have clear potential for growth and scalability." 
  },
  { 
    title: "Social Impact", 
    desc: "Preference for ventures that create positive social or environmental impact." 
  },
  { 
    title: "Legal Compliance", 
    desc: "Must operate within legal frameworks and ethical business practices." 
  }
])

// Success stories data
const successStories = ref([
  { 
    letter: "A", 
    name: "AgriTech Solutions", 
    quote: "The grant helped us develop our smart farming platform, now serving over 10,000 farmers across Nigeria.", 
    author: "Adebayo, Founder", 
    color: "from-blue-400 to-purple-500" 
  },
  { 
    letter: "E", 
    name: "EduConnect", 
    quote: "From a simple idea to reaching 50,000 students. Pitch Your Dream made our educational platform possible.", 
    author: "Emeka, CEO", 
    color: "from-green-400 to-blue-500" 
  },
  { 
    letter: "H", 
    name: "HealthFirst", 
    quote: "Our telemedicine platform now connects rural communities to quality healthcare nationwide.", 
    author: "Hauwa, Founder", 
    color: "from-purple-400 to-pink-500" 
  }
])

// Important dates data
const importantDates = ref([
  { 
    date: "Jan 15", 
    title: "Applications Open", 
    desc: "Registration begins for the next cohort", 
    color: "from-blue-500 to-purple-500" 
  },
  { 
    date: "Mar 31", 
    title: "Application Deadline", 
    desc: "Final day to submit applications", 
    color: "from-green-500 to-blue-500" 
  },
  { 
    date: "May 15", 
    title: "Pitch Day", 
    desc: "Selected candidates present their ideas", 
    color: "from-purple-500 to-pink-500" 
  },
  { 
    date: "Jun 1", 
    title: "Winners Announced", 
    desc: "Results and program commencement", 
    color: "from-orange-500 to-red-500" 
  }
])

// Contact information data
const contactInfo = ref([
  { 
    icon: Mail, 
    title: "Email Support", 
    desc: "pitchyourdream@mshelfoundation.org", 
    color: "from-blue-500 to-purple-500" 
  },
  { 
    icon: Phone, 
    title: "Phone Support", 
    desc: "+234 XXX XXX XXXX", 
    color: "from-green-500 to-blue-500" 
  },
  { 
    icon: MapPin, 
    title: "Office Visit", 
    desc: "Schedule an appointment", 
    color: "from-purple-500 to-pink-500" 
  }
])

// Form submission handler
const handleSubmit = () => {
  console.log('Form submitted:', formData)
  // Handle form submission logic here
  // You can add validation, API calls, etc.
}
</script>

<style scoped>
/* Add any custom styles here if needed */
.prose {
  max-width: none;
}
</style>